import {
  CustomViewComponent,
  CustomViewPageCategory,
  CustomViewPages,
} from '@agilelab/plugin-wb-platform';
import preview from './preview/practice_shaper_content/practice_shaper_content.png';
import thumbnail from './preview/practice_shaper_content/practice_shaper_content_thumbnail.png';

CustomViewPages.register({
  id: 'practice_shaper_content',
  meta: {
    label: 'Practice Shaper Detail Drawer',
    category: CustomViewPageCategory.PRACTICE_SHAPER,
    preview: {
      thumbnail: { src: thumbnail },
      items: [{ src: preview }],
    },
  },
  data: (
    <CustomViewComponent type="catalog_system_card">
      <CustomViewComponent type="container">
        {/* Common fields - always shown */}
        <CustomViewComponent type="string" label="URN" path="urn" colSpan="2" />
        <CustomViewComponent
          type="string"
          label="Description"
          path="entity.metadata.description"
          default="No description"
          colSpan="4"
        />

        {/* System Prototype fields - shown when kind is SystemPrototypeType */}
        <CustomViewComponent
          type="link"
          label="Template"
          path="entity.spec.template"
          colSpan="2"
          showWhen={{
            value: '{{ entity.kind }}',
            equals: 'SystemPrototypeType',
          }}
          showWhenExists="entity.spec.template"
        />
        <CustomViewComponent
          type="link"
          label="Evolves Into"
          path="entity.spec.evolvesInto"
          colSpan="2"
          showWhen={{
            value: '{{ entity.kind }}',
            equals: 'SystemPrototypeType',
          }}
          showWhenExists="entity.spec.evolvesInto"
        />

        {/* Non-Practice Shaper fields - shown when NOT a practice shaper entity */}
        <CustomViewComponent
          type="link"
          label="Owner"
          path="_relations.ownedByRelations"
          showWhen={{
            value: '{{ isPracticeShaperEntity }}',
            notEquals: 'true',
          }}
          showWhenExists="_relations.ownedByRelations.length"
          kind="group"
          colSpan="2"
        />
        <CustomViewComponent
          type="string"
          label="Lifecycle"
          path="entity.spec.lifecycle"
          showWhen={{
            value: '{{ isPracticeShaperEntity }}',
            notEquals: 'true',
          }}
          showWhenHasValue
          colSpan="2"
        />
        <CustomViewComponent
          type="string"
          label="Type"
          path="entity.spec.type"
          colSpan="2"
          showWhen={{
            value: '{{ isPracticeShaperEntity }}',
            notEquals: 'true',
          }}
          showWhenExists="entity.spec.type"
        />

        {/* System fields - shown when _types.isSystem is true */}
        <CustomViewComponent
          type="link"
          label="Domain"
          path="_relations.partOfDomainRelations"
          kind="domain"
          colSpan="2"
          showWhen={{
            value: '{{ _types.isSystem }}',
            equals: 'true',
          }}
        />
        <CustomViewComponent
          type="string"
          label="Partially Deployable In"
          path="partiallyDeployableIn"
          showWhen={{
            value: '{{ _types.isSystem }}',
            equals: 'true',
          }}
          showWhenHasValue
          colSpan="2"
        />

        {/* Component/API/Resource fields - shown when any of these types */}
        <CustomViewComponent
          type="link"
          label="System"
          path="_relations.partOfSystemRelations"
          kind="system"
          colSpan="2"
          default="No System"
          showWhen={{
            value:
              '{{ _types.isAPI or _types.isComponent or _types.isResource }}',
            equals: 'true',
          }}
        />

        {/* Component fields - shown when _types.isComponent */}
        <CustomViewComponent
          type="link"
          label="Parent Component"
          path="_relations.partOfComponentRelations"
          kind="domain"
          colSpan="2"
          default="No Parent Component"
          showWhen={{
            value: '{{ _types.isComponent }}',
            equals: 'true',
          }}
        />

        {/* Location fields - shown when _types.isLocation */}
        <CustomViewComponent
          type="link_grid_list"
          label="Targets"
          path="targets"
          showWhen={{
            value: '{{ _types.isLocation }}',
            equals: 'true',
          }}
          showWhenExists="targets"
        />

        {/* Taxonomy fields - shown for systems or headless components */}
        <CustomViewComponent
          type="link"
          label="{{ taxonomyLabel }}"
          path="taxonomies"
          colSpan="2"
          showWhen={{
            value: '{{ _types.isSystem or _types.isHeadlessComponent }}',
            equals: 'true',
          }}
        />

        {/* Practice Shaper fields - shown when isPracticeShaperEntity and has spec */}
        <CustomViewComponent
          type="automatic_fields_list"
          path="entity.spec"
          exclude={[
            'belongsTo',
            'partOfDomain',
            'partOfSystem',
            'template',
            'evolvesInto',
          ]}
          defaults={{
            _default: { colSpan: '2' },
            description: { colSpan: '4' },
          }}
          showWhen={{
            value: '{{ isPracticeShaperEntity and entity.spec is defined }}',
            equals: 'true',
          }}
        />

        {/* ComponentType specific practice shaper fields */}
        <CustomViewComponent
          type="link"
          label="Part of system"
          path="entity.spec.partOfSystem"
          colSpan="2"
          showWhen={{
            value:
              '{{ isPracticeShaperEntity and entity.kind == ComponentType }}',
            equals: 'true',
          }}
          showWhenExists="entity.spec.partOfSystem"
        />
        <CustomViewComponent
          type="link"
          mode="linear"
          label="Traits"
          path="implementsRelations"
          colSpan="2"
          showWhen={{
            value:
              '{{ isPracticeShaperEntity and entity.kind == ComponentType}}',
            equals: 'true',
          }}
          showWhenExists="implementsRelations"
        />

        {/* SystemType specific practice shaper fields */}
        <CustomViewComponent
          type="link"
          label="part of domain"
          path="entity.spec.partOfDomain"
          colSpan="2"
          showWhen={{
            value: '{{ isPracticeShaperEntity and entity.kind == SystemType }}',
            equals: 'true',
          }}
          showWhenExists="entity.spec.partOfDomain"
        />
        <CustomViewComponent
          type="link"
          label="belongs to"
          path="entity.spec.belongsTo"
          colSpan="2"
          showWhen={{
            value: '{{ isPracticeShaperEntity and entity.kind == SystemType }}',
            equals: 'true',
          }}
          showWhenExists="entity.spec.belongsTo"
        />
        <CustomViewComponent
          type="link"
          mode="linear"
          label="Traits"
          path="implementsRelations"
          colSpan="2"
          showWhen={{
            value: '{{ isPracticeShaperEntity and entity.kind == SystemType }}',
            equals: 'true',
          }}
          showWhenExists="implementsRelations"
        />

        {/* DomainType fields */}
        <CustomViewComponent
          type="link"
          mode="linear"
          label="Traits"
          path="implementsRelations"
          colSpan="2"
          showWhen={{
            value: '{{ entity.kind }}',
            equals: 'DomainType',
          }}
          showWhenExists="implementsRelations"
        />

        {/* TaxonomyType fields */}
        <CustomViewComponent
          type="link"
          mode="linear"
          label="Traits"
          path="implementsRelations"
          colSpan="2"
          showWhen={{
            value: '{{ entity.kind }}',
            equals: 'TaxonomyType',
          }}
          showWhenExists="implementsRelations"
        />
        <CustomViewComponent
          type="hline"
          showWhen={{
            value: '{{ tags.length }}',
            notEquals: 0,
          }}
        />
        <CustomViewComponent
          type="tags"
          label="Tags"
          path="tags"
          showWhen={{
            value: '{{ tags.length }}',
            notEquals: 0,
          }}
        />
      </CustomViewComponent>
    </CustomViewComponent>
  ),
});
